import { SkaterWaiver, Waiver as WaiverType } from '@types';
import { MobileSelect, SignaturePad } from '_atoms';
import { SeasonDetailsService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import {
  compressSignature,
  getCompressionSettings,
} from '_utils/signatureCompression';
import jsPDF from 'jspdf';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MdAdd,
  MdCheckCircle,
  MdClear,
  MdDownload,
  MdEdit,
  MdExpandMore,
  MdGavel,
  MdPerson,
  MdRemove,
} from 'react-icons/md';

// Cache for waiver data to prevent multiple API calls
let waiverCache: {
  data: WaiverType | null;
  timestamp: number;
  promise: Promise<WaiverType> | null;
} = {
  data: null,
  timestamp: 0,
  promise: null,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Function to clear the waiver cache (useful for testing or manual refresh)
export const clearWaiverCache = () => {
  waiverCache = {
    data: null,
    timestamp: 0,
    promise: null,
  };
};

// Custom hook for memoized waiver fetching
const useMemoizedWaiver = () => {
  const [currentWaiver, setCurrentWaiver] = useState<WaiverType>({
    id: '',
    waiverDetails: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentWaiver = async (forceRefresh = false) => {
    const now = Date.now();

    // If force refresh, clear cache
    if (forceRefresh) {
      waiverCache.data = null;
      waiverCache.timestamp = 0;
      waiverCache.promise = null;
    }

    // Check if we have valid cached data
    if (waiverCache.data && now - waiverCache.timestamp < CACHE_DURATION) {
      setCurrentWaiver(waiverCache.data);
      setLoading(false);
      setError(null);
      return;
    }

    // Check if there's already a pending request
    if (waiverCache.promise) {
      try {
        const responseData = await waiverCache.promise;
        setCurrentWaiver(responseData);
        setLoading(false);
        setError(null);
      } catch (error) {
        console.error('Error fetching waiver from cache:', error);
        setLoading(false);
        setError('Failed to load waiver. Please try again.');
      }
      return;
    }

    // Make new API call
    setLoading(true);
    setError(null);
    const fetchPromise =
      SeasonDetailsService.getCurrentWaiver() as unknown as Promise<WaiverType>;
    waiverCache.promise = fetchPromise;

    try {
      const responseData = (await fetchPromise) as WaiverType;
      console.log('Waiver details:', responseData.waiverDetails);

      // Update cache
      waiverCache.data = responseData;
      waiverCache.timestamp = now;
      waiverCache.promise = null;

      setCurrentWaiver(responseData);
      setError(null);
    } catch (error) {
      console.error('Error fetching waiver:', error);
      waiverCache.promise = null;
      setError('Failed to load waiver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentWaiver();
  }, []);

  const refetchWaiver = () => {
    fetchCurrentWaiver(true);
  };

  return { currentWaiver, loading, error, refetchWaiver };
};

interface WaiverProps {
  waiverAccepted?: boolean;
  skatersInfo?: any[];
  onNext: (data?: any) => void;
  onPrevious?: () => void;
  onStepComplete: (data: any) => void;
  continueButtonText?: string;
  isSubmitting?: boolean;
  submitError?: string;
}

export const Waiver: React.FC<WaiverProps> = ({
  waiverAccepted = false,
  skatersInfo = [],
  onNext,
  onPrevious,
  onStepComplete,
  continueButtonText = 'Continue',
  isSubmitting = false,
  submitError = '',
}) => {
  const { currentWaiver, loading, error, refetchWaiver } = useMemoizedWaiver();
  // State for participant type selection
  type ParticipantType = 'adult' | 'minors' | 'adult-and-minors' | null;
  const [participantType, setParticipantType] = useState<ParticipantType>(null);

  // Track if we've already initialized default waivers for empty skatersInfo
  const hasInitializedDefaultWaivers = useRef(false);

  const [skaterWaivers, setSkaterWaivers] = useState<SkaterWaiver[]>(() => {
    // If no skatersInfo provided, return empty array initially
    if (skatersInfo.length === 0) {
      return [];
    }

    // Initialize waivers for each skater
    const waivers: SkaterWaiver[] = skatersInfo.map(skater => {
      const age = HelperMethods.calculateAge(skater.dateOfBirth);
      const isAdult = age >= 18;
      return {
        skaterId: skater.id,
        skaterName: `${skater.firstName} ${skater.lastName}`,
        dateOfBirth: skater.dateOfBirth,
        isAdult,
        hasReadWaiver: false,
        accepted: false,
        signature: '',
        signedBy: '',
        relationship: isAdult ? 'self' : '',
        email: skater.email || '',
        phoneNumber: skater.phoneNumber || '',
        emergencyContactPhone: '',
        signedPDF: null,
        minors: [],
        waiverId: currentWaiver.id,
      };
    });

    // Automatically assign all minors to the first adult
    const adults = waivers.filter(waiver => waiver.isAdult);
    const minors = waivers.filter(waiver => !waiver.isAdult);

    if (adults.length > 0 && minors.length > 0) {
      const firstAdult = adults[0];
      const minorIds: string[] = minors.map(minor => minor.skaterId);

      // Add all minors to the first adult
      firstAdult.minors = minorIds;

      // Update minors with guardian relationship
      minors.forEach(minor => {
        minor.relationship = 'guardian';
      });
    }

    return waivers;
  });
  const [showExpandedWaiver, setShowExpandedWaiver] = useState(false);

  // State for standalone waiver form (when no skatersInfo)
  const [standaloneWaiver, setStandaloneWaiver] = useState({
    hasReadWaiver: false,
    accepted: false,
    signedBy: '',
    relationship: 'self',
    email: '',
    phoneNumber: '',
    emergencyContactPhone: '',
    signature: '',
  });

  // Memoize skater waivers initialization to prevent unnecessary re-renders
  const initialSkaterWaivers = useMemo(() => {
    // If no skatersInfo provided, return empty array
    if (skatersInfo.length === 0) {
      return [];
    }

    const waivers: SkaterWaiver[] = skatersInfo.map(skater => {
      const age = HelperMethods.calculateAge(skater.dateOfBirth);
      const isAdult = age >= 18 || skater.isAdult;
      return {
        skaterId: skater.id,
        skaterName: `${skater.firstName} ${skater.lastName}`,
        dateOfBirth: skater.dateOfBirth,
        isAdult,
        hasReadWaiver: false,
        accepted: false,
        signature: '',
        signedBy: '',
        relationship: isAdult ? 'self' : '',
        email: skater.email || '',
        phoneNumber: skater.phoneNumber || '',
        emergencyContactPhone: '',
        signedPDF: null,
        minors: [],
        waiverId: currentWaiver.id,
      };
    });

    // Automatically assign all minors to the first adult
    const adults = waivers.filter(waiver => waiver.isAdult);
    const minors = waivers.filter(waiver => !waiver.isAdult);

    if (adults.length > 0 && minors.length > 0) {
      const firstAdult = adults[0];
      const minorIds: string[] = minors.map(minor => minor.skaterId);

      // Add all minors to the first adult
      firstAdult.minors = minorIds;

      // Update minors with guardian relationship
      minors.forEach(minor => {
        minor.relationship = 'guardian';
      });
    }

    return waivers;
  }, [skatersInfo, currentWaiver.id]);

  // Update skater waivers if skatersInfo changes
  useEffect(() => {
    setSkaterWaivers(initialSkaterWaivers);
  }, [initialSkaterWaivers]);

  // Update waiverId when currentWaiver is loaded
  useEffect(() => {
    if (currentWaiver.id && skaterWaivers.length > 0) {
      setSkaterWaivers(prevWaivers =>
        prevWaivers.map(waiver => ({
          ...waiver,
          waiverId: currentWaiver.id,
        })),
      );
    }
  }, [currentWaiver.id, skaterWaivers.length]);

  // Auto-detect participant type based on skater waivers (only when skatersInfo is provided)
  useEffect(() => {
    if (
      skatersInfo.length > 0 &&
      skaterWaivers.length > 0 &&
      participantType === null
    ) {
      const adults = skaterWaivers.filter(w => w.isAdult);
      const minors = skaterWaivers.filter(w => !w.isAdult);

      if (adults.length > 0 && minors.length > 0) {
        setParticipantType('adult-and-minors');
      } else if (adults.length > 0 && minors.length === 0) {
        setParticipantType('adult');
      } else if (minors.length > 0 && adults.length === 0) {
        setParticipantType('minors');
      }
    }
  }, [skatersInfo.length, skaterWaivers, participantType]);

  // Create default waivers when participant type is selected and no skatersInfo provided
  useEffect(() => {
    if (skatersInfo.length === 0 && participantType !== null) {
      // Clear existing waivers and reset flag
      hasInitializedDefaultWaivers.current = false;

      const defaultWaivers: SkaterWaiver[] = [];

      if (participantType === 'adult') {
        // Single adult waiver
        defaultWaivers.push({
          skaterId: 'default-adult-1',
          skaterName: '',
          dateOfBirth: '',
          isAdult: true,
          hasReadWaiver: false,
          accepted: false,
          signature: '',
          signedBy: '',
          relationship: 'self',
          email: '',
          phoneNumber: '',
          emergencyContactPhone: '',
          signedPDF: null,
          minors: [],
          waiverId: currentWaiver.id,
        });
      } else if (participantType === 'minors') {
        // Single minor waiver (guardian will sign)
        defaultWaivers.push({
          skaterId: 'default-minor-1',
          skaterName: '',
          dateOfBirth: '',
          isAdult: false,
          hasReadWaiver: false,
          accepted: false,
          signature: '',
          signedBy: '',
          relationship: 'guardian',
          email: '',
          phoneNumber: '',
          emergencyContactPhone: '',
          signedPDF: null,
          minors: [],
          waiverId: currentWaiver.id,
        });
      } else if (participantType === 'adult-and-minors') {
        // One adult and one minor
        const adultWaiver: SkaterWaiver = {
          skaterId: 'default-adult-1',
          skaterName: '',
          dateOfBirth: '',
          isAdult: true,
          hasReadWaiver: false,
          accepted: false,
          signature: '',
          signedBy: '',
          relationship: 'self',
          email: '',
          phoneNumber: '',
          emergencyContactPhone: '',
          signedPDF: null,
          minors: ['default-minor-1'],
          waiverId: currentWaiver.id,
        };

        const minorWaiver: SkaterWaiver = {
          skaterId: 'default-minor-1',
          skaterName: '',
          dateOfBirth: '',
          isAdult: false,
          hasReadWaiver: false,
          accepted: false,
          signature: '',
          signedBy: '',
          relationship: 'guardian',
          email: '',
          phoneNumber: '',
          emergencyContactPhone: '',
          signedPDF: null,
          minors: [],
          waiverId: currentWaiver.id,
        };

        defaultWaivers.push(adultWaiver, minorWaiver);
      }

      setSkaterWaivers(defaultWaivers);
      hasInitializedDefaultWaivers.current = true;
    }
  }, [skatersInfo.length, participantType, currentWaiver.id]);

  // Get available minors for adults to add
  const getAvailableMinors = (adultSkaterId: string) => {
    return skaterWaivers.filter(waiver => {
      const age = HelperMethods.calculateAge(waiver.dateOfBirth);
      const isMinor = age < 18;
      const isAlreadyAdded = skaterWaivers.some(
        adultWaiver =>
          adultWaiver.isAdult && adultWaiver.minors.includes(waiver.skaterId),
      );
      return isMinor && !isAlreadyAdded && waiver.skaterId !== adultSkaterId;
    });
  };

  // Add minor to adult's waiver
  const addMinorToWaiver = (adultSkaterId: string, minorSkaterId: string) => {
    setSkaterWaivers(prev => {
      // Find the adult's waiver to get their signature info
      const adultWaiver = prev.find(w => w.skaterId === adultSkaterId);

      return prev.map(waiver => {
        if (waiver.skaterId === adultSkaterId) {
          // Add minor to adult's minors list
          return { ...waiver, minors: [...waiver.minors, minorSkaterId] };
        } else if (waiver.skaterId === minorSkaterId) {
          // Copy adult's waiver information to the minor's waiver
          return {
            ...waiver,
            hasReadWaiver: adultWaiver?.hasReadWaiver || false,
            accepted: adultWaiver?.accepted || false,
            signature: adultWaiver?.signature || '',
            signedBy: adultWaiver?.signedBy || '',
            relationship: 'guardian', // Set relationship to guardian for minors
            email: adultWaiver?.email || '',
            phoneNumber: adultWaiver?.phoneNumber || '',
            emergencyContactPhone: adultWaiver?.emergencyContactPhone || '',
          };
        }
        return waiver;
      });
    });
  };

  // Remove minor from adult's waiver
  const removeMinorFromWaiver = (
    adultSkaterId: string,
    minorSkaterId: string,
  ) => {
    setSkaterWaivers(prev =>
      prev.map(waiver => {
        if (waiver.skaterId === adultSkaterId) {
          // Remove minor from adult's minors list
          return {
            ...waiver,
            minors: waiver.minors.filter(minor => minor !== minorSkaterId),
          };
        } else if (waiver.skaterId === minorSkaterId) {
          // Clear the minor's waiver information when removed from adult
          return {
            ...waiver,
            hasReadWaiver: false,
            accepted: false,
            signature: '',
            signedBy: '',
            relationship: '',
            email: '',
            phoneNumber: '',
            emergencyContactPhone: '',
          };
        }
        return waiver;
      }),
    );
  };

  // Add a new minor when no skatersInfo is provided
  const addNewMinor = () => {
    const minorCount = skaterWaivers.filter(w => !w.isAdult).length;
    const newMinorId = `default-minor-${minorCount + 1}`;
    const newMinorWaiver: SkaterWaiver = {
      skaterId: newMinorId,
      skaterName: '',
      dateOfBirth: '',
      isAdult: false,
      hasReadWaiver: false,
      accepted: false,
      signature: '',
      signedBy: '',
      relationship: 'guardian',
      email: '',
      phoneNumber: '',
      emergencyContactPhone: '',
      signedPDF: null,
      minors: [],
      waiverId: currentWaiver.id,
    };

    // If there's an adult, add this minor to their minors list
    setSkaterWaivers(prev => {
      const adults = prev.filter(w => w.isAdult);
      if (adults.length > 0) {
        return prev
          .map(w => {
            if (w.isAdult) {
              return { ...w, minors: [...w.minors, newMinorId] };
            }
            return w;
          })
          .concat(newMinorWaiver);
      }
      // If no adults (minors only scenario), just add the minor
      return [...prev, newMinorWaiver];
    });
  };

  // Remove a minor when no skatersInfo is provided
  const removeMinor = (skaterId: string) => {
    setSkaterWaivers(prev => {
      // Remove the minor from any adult's minors list and from the waivers array
      return prev
        .map(w => {
          if (w.isAdult && w.minors.includes(skaterId)) {
            return { ...w, minors: w.minors.filter(id => id !== skaterId) };
          }
          return w;
        })
        .filter(w => w.skaterId !== skaterId);
    });
  };

  // Update minor's name
  const updateMinorName = (
    skaterId: string,
    firstName: string,
    lastName: string,
  ) => {
    setSkaterWaivers(prev =>
      prev.map(waiver =>
        waiver.skaterId === skaterId
          ? { ...waiver, skaterName: `${firstName} ${lastName}`.trim() }
          : waiver,
      ),
    );
  };

  // Update minor's date of birth
  const updateMinorDateOfBirth = (skaterId: string, dateOfBirth: string) => {
    // For type="date" inputs, browser handles validation automatically
    setSkaterWaivers(prev =>
      prev.map(waiver =>
        waiver.skaterId === skaterId ? { ...waiver, dateOfBirth } : waiver,
      ),
    );
  };

  // State for managing the minor selection dropdown
  const [showMinorDropdown, setShowMinorDropdown] = useState<string | null>(
    null,
  );

  // Handle adding a minor to an adult's waiver
  const handleAddMinor = (adultSkaterId: string, minorSkaterId: string) => {
    addMinorToWaiver(adultSkaterId, minorSkaterId);
    setShowMinorDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMinorDropdown &&
        !(event.target as Element).closest('.minor-dropdown')
      ) {
        setShowMinorDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMinorDropdown]);

  // Use refs to store signature pad references for clear functionality
  const signaturePadRefs = useRef<{ [key: string]: any }>({});

  const updateSkaterWaiver = (
    skaterId: string,
    field: keyof SkaterWaiver,
    value: string | boolean | string[],
  ) => {
    setSkaterWaivers(prev => {
      const updatedWaivers = prev.map(waiver =>
        waiver.skaterId === skaterId ? { ...waiver, [field]: value } : waiver,
      );

      // If this is an adult and we're updating waiver-related fields, also update their minors
      const adultWaiver = updatedWaivers.find(w => w.skaterId === skaterId);
      if (
        adultWaiver?.isAdult &&
        [
          'hasReadWaiver',
          'accepted',
          'signature',
          'signedBy',
          'relationship',
          'email',
          'phoneNumber',
          'emergencyContactPhone',
        ].includes(field)
      ) {
        return updatedWaivers.map(waiver => {
          // If this waiver is a minor of the adult, copy the adult's waiver info
          if (adultWaiver.minors.includes(waiver.skaterId)) {
            return {
              ...waiver,
              hasReadWaiver: adultWaiver.hasReadWaiver,
              accepted: adultWaiver.accepted,
              signature: adultWaiver.signature,
              signedBy: adultWaiver.signedBy,
              signedPDF: adultWaiver.signedPDF,
              relationship: 'guardian', // Set relationship to guardian for minors
              email: adultWaiver.email,
              phoneNumber: adultWaiver.phoneNumber,
              emergencyContactPhone: adultWaiver.emergencyContactPhone,
            };
          }
          return waiver;
        });
      }

      return updatedWaivers;
    });
  };

  const clearSignature = (skaterId: string) => {
    const signaturePad = signaturePadRefs.current[skaterId];
    if (signaturePad) {
      signaturePad.clear();
      updateSkaterWaiver(skaterId, 'signature', '');
    }
  };

  const setSignaturePadRef = (skaterId: string, ref: any) => {
    if (ref) {
      signaturePadRefs.current[skaterId] = ref;
    }
  };

  const handleAutoSave = async (skaterId: string, dataUrl: string) => {
    try {
      // Compress the signature for storage
      const compressedDataUrl = await compressSignature(
        dataUrl,
        getCompressionSettings('storage'),
      );
      updateSkaterWaiver(skaterId, 'signature', compressedDataUrl);
    } catch (error) {
      console.error('Error compressing signature:', error);
      // Fallback to original data URL if compression fails
      updateSkaterWaiver(skaterId, 'signature', dataUrl);
    }
  };

  const compressSignatureForPDF = async (
    signatureDataUrl: string,
  ): Promise<string> => {
    try {
      return await compressSignature(
        signatureDataUrl,
        getCompressionSettings('pdf'),
      );
    } catch (error) {
      console.error('Error compressing signature for PDF:', error);
      return signatureDataUrl; // Fallback to original
    }
  };

  // Helper function to convert HTML to plain text
  const htmlToPlainText = (html: string): string => {
    if (!html) return '';

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Convert to plain text
    let text = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  };

  // Helper function to split text into lines for PDF
  const splitTextIntoLines = (
    text: string,
    maxWidth: number,
    pdf: any,
  ): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = pdf.getTextWidth(testLine);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };

  // Generate individual signed PDF for a specific skater
  const generateIndividualSignedPDF = async (
    waiver: SkaterWaiver,
  ): Promise<File> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Add title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Skating Rink Liability Waiver', pageWidth / 2, yPosition, {
      align: 'center',
    });
    yPosition += 15;

    // Add date
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString();
    pdf.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 20;

    // Skater name
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    waiver.isAdult
      ? pdf.text(`Waiver for: ${waiver.skaterName}`, margin, yPosition)
      : pdf.text(
          `Waiver for: ${waiver.skaterName} (Age: ${HelperMethods.calculateAge(waiver.dateOfBirth)})`,
          margin,
          yPosition,
        );
    yPosition += 10;

    // Add minors information if this is an adult with minors or if this is a guardian waiver for minors
    if (
      (waiver.isAdult && waiver.minors.length > 0) ||
      (hasOnlyMinors && waiver.minors.length > 0)
    ) {
      console.log('Adding minors to PDF:', {
        waiverName: waiver.skaterName,
        waiverMinors: waiver.minors,
        hasOnlyMinors,
        allSkaterWaivers: skaterWaivers.map(w => ({
          name: w.skaterName,
          id: w.skaterId,
          minors: w.minors,
        })),
      });

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Minors covered by this waiver:', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      for (const minorId of waiver.minors) {
        const minorWaiver = skaterWaivers.find(w => w.skaterId === minorId);
        if (minorWaiver) {
          pdf.text(
            `- ${minorWaiver.skaterName} (Age: ${HelperMethods.calculateAge(minorWaiver.dateOfBirth)})`,
            margin + 5,
            yPosition,
          );
          yPosition += 5;
        }
      }
      yPosition += 5;
    }

    // Waiver content
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Convert HTML to plain text and format for PDF
    if (!currentWaiver.waiverDetails) {
      console.error('No waiver details available for PDF generation');
      throw new Error('No waiver details available');
    }

    const waiverText = htmlToPlainText(currentWaiver.waiverDetails);

    const maxWidth = pageWidth - 2 * margin;
    const lines = splitTextIntoLines(waiverText, maxWidth, pdf);

    for (const line of lines) {
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    }

    yPosition += 10;

    // Signature information
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Signature Information:', margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Signed by: ${waiver.signedBy}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Relationship: ${waiver.relationship}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Email: ${waiver.email}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Phone: ${waiver.phoneNumber}`, margin, yPosition);
    yPosition += 5;
    pdf.text(
      `Emergency Contact: ${waiver.emergencyContactPhone}`,
      margin,
      yPosition,
    );
    yPosition += 5;
    pdf.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 5;
    pdf.text(
      `Read and Accepted: ${waiver.hasReadWaiver ? 'Yes' : 'No'}`,
      margin,
      yPosition,
    );
    yPosition += 5;
    pdf.text(
      `Terms Accepted: ${waiver.accepted ? 'Yes' : 'No'}`,
      margin,
      yPosition,
    );
    yPosition += 15;

    // Add signature image if available
    if (waiver.signature) {
      try {
        // Compress signature specifically for PDF
        const compressedSignature = await compressSignatureForPDF(
          waiver.signature,
        );

        const signatureImg = new Image();
        signatureImg.src = compressedSignature;

        await new Promise(resolve => {
          signatureImg.onload = () => {
            const imgWidth = 60;
            const imgHeight =
              (signatureImg.height * imgWidth) / signatureImg.width;

            if (yPosition + imgHeight > pageHeight - 20) {
              pdf.addPage();
              yPosition = margin;
            }

            pdf.text('Digital Signature:', margin, yPosition);
            yPosition += 5;
            pdf.addImage(
              compressedSignature,
              'JPEG',
              margin,
              yPosition,
              imgWidth,
              imgHeight,
            );
            yPosition += imgHeight + 10;
            resolve(true);
          };
          signatureImg.onerror = () => {
            pdf.text(
              'Digital Signature: [Signature captured]',
              margin,
              yPosition,
            );
            yPosition += 10;
            resolve(true);
          };
        });
      } catch (error) {
        pdf.text('Digital Signature: [Signature captured]', margin, yPosition);
        yPosition += 10;
      }
    }

    // Generate filename for individual waiver
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let skaterName = waiver.skaterName.replace(/\s+/g, '_');

    // Include minors' names in filename if this is an adult with minors or guardian waiver for minors
    if (
      (waiver.isAdult && waiver.minors.length > 0) ||
      (hasOnlyMinors && waiver.minors.length > 0)
    ) {
      const minorNames = waiver.minors
        .map(minorId => {
          const minorWaiver = skaterWaivers.find(w => w.skaterId === minorId);
          return minorWaiver ? minorWaiver.skaterName.replace(/\s+/g, '_') : '';
        })
        .filter(name => name)
        .join('_');
      if (minorNames) {
        skaterName = `${skaterName}_with_${minorNames}`;
      }
    }

    const fileName = `waiver_${skaterName}_${timestamp}.pdf`;

    // Convert PDF to File object
    const pdfBlob = pdf.output('blob');
    const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

    console.log('Generated PDF File:', {
      name: pdfFile.name,
      size: pdfFile.size,
      type: pdfFile.type,
      lastModified: pdfFile.lastModified,
    });

    return pdfFile;
  };

  // Generate and download individual PDF for a specific skater
  const downloadIndividualPDF = async (waiver: SkaterWaiver) => {
    try {
      const pdfFile = await generateIndividualSignedPDF(waiver);

      // Create download link
      const url = URL.createObjectURL(pdfFile);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating individual PDF:', error);
    }
  };

  const handleContinue = async () => {
    console.log('handleContinue called, currentWaiver:', currentWaiver);
    console.log('skaterWaivers:', skaterWaivers);

    // If there are only minors, check if all minors have guardian waiver completed
    if (hasOnlyMinors) {
      const allMinorsComplete = skaterWaivers.every(
        waiver =>
          waiver.hasReadWaiver &&
          waiver.accepted &&
          waiver.signature.trim() &&
          waiver.signedBy.trim() &&
          waiver.relationship.trim() &&
          waiver.email.trim() &&
          HelperMethods.isValidEmail(waiver.email) &&
          waiver.phoneNumber.trim() &&
          waiver.emergencyContactPhone.trim(),
      );

      if (allMinorsComplete) {
        try {
          // Generate signed PDFs for each minor
          console.log('Generating PDFs for minors...');
          const updatedSkaterWaivers = await Promise.all(
            skaterWaivers.map(async waiver => {
              try {
                console.log(`Starting PDF generation for ${waiver.skaterName}`);
                const signedPDF = await generateIndividualSignedPDF(waiver);
                console.log(
                  `Generated PDF for ${waiver.skaterName}:`,
                  signedPDF,
                );
                return { ...waiver, signedPDF };
              } catch (error) {
                console.error(
                  `Error generating PDF for ${waiver.skaterName}:`,
                  error,
                );
                return { ...waiver, signedPDF: null };
              }
            }),
          );

          setSkaterWaivers(updatedSkaterWaivers);
          console.log('Final skaterWaivers payload:', updatedSkaterWaivers);

          onStepComplete({
            waiverAccepted: true,
            skaterWaivers: updatedSkaterWaivers,
          });
          onNext({
            waiverAccepted: true,
            skaterWaivers: updatedSkaterWaivers,
          });
        } catch (error) {
          console.error('Error in handleContinue:', error);
        }
        return;
      }
    }

    // Check if all adults have completed waivers and all minors are covered
    const allAdultsComplete = skaterWaivers
      .filter(waiver => waiver.isAdult)
      .every(waiver => {
        // For adults without minors, relationship should be "self"
        const validRelationship =
          waiver.minors.length === 0
            ? waiver.relationship === 'self'
            : waiver.relationship.trim();

        return (
          waiver.hasReadWaiver &&
          waiver.accepted &&
          waiver.signature.trim() &&
          waiver.signedBy.trim() &&
          validRelationship &&
          waiver.email.trim() &&
          HelperMethods.isValidEmail(waiver.email) &&
          waiver.phoneNumber.trim() &&
          waiver.emergencyContactPhone.trim()
        );
      });

    // Check if all minors are covered by an adult's waiver
    const allMinorsCovered = skaterWaivers
      .filter(waiver => !waiver.isAdult)
      .every(minorWaiver => {
        return skaterWaivers.some(
          adultWaiver =>
            adultWaiver.isAdult &&
            adultWaiver.minors.includes(minorWaiver.skaterId),
        );
      });

    if (allAdultsComplete && allMinorsCovered) {
      try {
        // Generate signed PDFs for each adult (including their minors)
        console.log('Generating PDFs for adults...');
        const updatedSkaterWaivers = await Promise.all(
          skaterWaivers.map(async waiver => {
            if (waiver.isAdult) {
              try {
                console.log(
                  `Starting PDF generation for adult ${waiver.skaterName}`,
                );
                // Always generate a fresh PDF for adults to ensure it's up to date
                const signedPDF = await generateIndividualSignedPDF(waiver);
                console.log(
                  `Generated PDF for ${waiver.skaterName}:`,
                  signedPDF,
                );
                return { ...waiver, signedPDF };
              } catch (error) {
                console.error(
                  `Error generating PDF for ${waiver.skaterName}:`,
                  error,
                );
                return { ...waiver, signedPDF: null };
              }
            }
            return waiver;
          }),
        );

        // Update state with the generated PDFs
        setSkaterWaivers(updatedSkaterWaivers);

        console.log('Final skaterWaivers payload:', updatedSkaterWaivers);

        onStepComplete({
          waiverAccepted: true,
          skaterWaivers: updatedSkaterWaivers,
        });
        onNext({
          waiverAccepted: true,
          skaterWaivers: updatedSkaterWaivers,
        });
      } catch (error) {
        console.error('Error in handleContinue:', error);
      }
    }
  };

  const isFormValid = () => {
    // If there are only minors, check if all minors have guardian waiver completed
    if (hasOnlyMinors) {
      return skaterWaivers.every(
        waiver =>
          waiver.hasReadWaiver &&
          waiver.accepted &&
          waiver.signature.trim() &&
          waiver.signedBy.trim() &&
          waiver.relationship.trim() &&
          waiver.email.trim() &&
          waiver.phoneNumber.trim() &&
          waiver.emergencyContactPhone.trim(),
      );
    }

    // Check if all adults have completed waivers
    const allAdultsComplete = skaterWaivers
      .filter(waiver => waiver.isAdult)
      .every(waiver => {
        // For adults without minors, relationship should be "self"
        const validRelationship =
          waiver.minors.length === 0
            ? waiver.relationship === 'self'
            : waiver.relationship.trim();

        return (
          waiver.hasReadWaiver &&
          waiver.accepted &&
          waiver.signature.trim() &&
          waiver.signedBy.trim() &&
          validRelationship &&
          waiver.email.trim() &&
          waiver.phoneNumber.trim() &&
          waiver.emergencyContactPhone.trim()
        );
      });

    // Check if all minors are covered by an adult's waiver
    const allMinorsCovered = skaterWaivers
      .filter(waiver => !waiver.isAdult)
      .every(minorWaiver => {
        return skaterWaivers.some(
          adultWaiver =>
            adultWaiver.isAdult &&
            adultWaiver.minors.includes(minorWaiver.skaterId),
        );
      });

    return allAdultsComplete && allMinorsCovered;
  };

  // Check if there are only minors (no adults)
  const hasOnlyMinors =
    (skaterWaivers.length > 0 &&
      skaterWaivers.every(waiver => !waiver.isAdult)) ||
    (participantType === 'minors' && skatersInfo.length === 0);

  // Helper function to ensure minors are properly assigned to the first minor
  const ensureMinorsAssigned = useCallback(() => {
    if (hasOnlyMinors && skaterWaivers.length > 1) {
      const firstMinorId = skaterWaivers[0].skaterId;
      const allMinorIds = skaterWaivers.map(waiver => waiver.skaterId);
      const expectedMinors = allMinorIds.filter(id => id !== firstMinorId);

      const firstMinor = skaterWaivers.find(w => w.skaterId === firstMinorId);
      if (
        !firstMinor ||
        !firstMinor.minors ||
        firstMinor.minors.length !== expectedMinors.length
      ) {
        console.log('Ensuring minors are assigned to first minor:', {
          firstMinorId,
          allMinorIds,
          expectedMinors,
          currentMinors: firstMinor?.minors || [],
        });

        setSkaterWaivers(prev =>
          prev.map(waiver => {
            if (waiver.skaterId === firstMinorId) {
              return {
                ...waiver,
                minors: expectedMinors,
              };
            }
            return waiver;
          }),
        );
        return true; // Indicates that we made changes
      }
    }
    return false; // No changes needed
  }, [hasOnlyMinors, skaterWaivers]);

  // When there are only minors, automatically add all minors to the first minor's waiver
  useEffect(() => {
    ensureMinorsAssigned();
  }, [ensureMinorsAssigned]);

  // Filter to show only adults and minors that aren't already added to an adult's waiver
  const displayWaivers = skaterWaivers.filter(waiver => {
    if (waiver.isAdult) return true;
    // Only show minors that aren't already added to an adult's waiver
    return !skaterWaivers.some(
      adultWaiver =>
        adultWaiver.isAdult && adultWaiver.minors.includes(waiver.skaterId),
    );
  });

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Liability Waiver
        </h2>
        <p className="text-gray-600">
          {skatersInfo.length === 0
            ? 'Please read and sign the liability waiver below.'
            : hasOnlyMinors
              ? 'All skaters are minors. A guardian must sign the waiver on their behalf.'
              : "Adults (18+) must sign waivers. Minors can be added to an adult's waiver."}
        </p>
      </div>

      {/* Participant Type Selection */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4 text-center">
          {participantType !== null
            ? 'Change participant type (this will clear your current form)'
            : 'Please select who will be participating'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
          {/* Adult Option */}
          <button
            type="button"
            onClick={() => setParticipantType('adult')}
            className={`relative p-3 md:p-6 rounded-lg border-2 transition-all duration-200 ${
              participantType === 'adult'
                ? 'border-blue-600 bg-blue-100 shadow-md'
                : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-sm'
            }`}
          >
            <div className="flex md:flex-col items-center md:space-y-3 space-x-3 md:space-x-0">
              <MdPerson
                className={`w-8 h-8 md:w-12 md:h-12 flex-shrink-0 ${
                  participantType === 'adult'
                    ? 'text-blue-600'
                    : 'text-gray-500'
                }`}
              />
              <div className="text-left md:text-center flex-1">
                <h4
                  className={`font-semibold text-base md:text-lg ${
                    participantType === 'adult'
                      ? 'text-blue-900'
                      : 'text-gray-700'
                  }`}
                >
                  Adult
                </h4>
                <p className="text-xs md:text-sm text-gray-600 md:mt-1">
                  One adult (18+)
                </p>
              </div>
              {participantType === 'adult' && (
                <div className="md:absolute md:top-2 md:right-2">
                  <MdCheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
              )}
            </div>
          </button>

          {/* Minors Option */}
          <button
            type="button"
            onClick={() => setParticipantType('minors')}
            className={`relative p-3 md:p-6 rounded-lg border-2 transition-all duration-200 ${
              participantType === 'minors'
                ? 'border-blue-600 bg-blue-100 shadow-md'
                : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-sm'
            }`}
          >
            <div className="flex md:flex-col items-center md:space-y-3 space-x-3 md:space-x-0">
              <div className="flex flex-shrink-0">
                <MdPerson
                  className={`w-7 h-7 md:w-10 md:h-10 ${
                    participantType === 'minors'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                />
                <MdPerson
                  className={`w-7 h-7 md:w-10 md:h-10 -ml-2 md:-ml-3 ${
                    participantType === 'minors'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                />
              </div>
              <div className="text-left md:text-center flex-1">
                <h4
                  className={`font-semibold text-base md:text-lg ${
                    participantType === 'minors'
                      ? 'text-blue-900'
                      : 'text-gray-700'
                  }`}
                >
                  Minor(s)
                </h4>
                <p className="text-xs md:text-sm text-gray-600 md:mt-1">
                  One or more minors
                </p>
              </div>
              {participantType === 'minors' && (
                <div className="md:absolute md:top-2 md:right-2">
                  <MdCheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
              )}
            </div>
          </button>

          {/* Adult and Minors Option */}
          <button
            type="button"
            onClick={() => setParticipantType('adult-and-minors')}
            className={`relative p-3 md:p-6 rounded-lg border-2 transition-all duration-200 ${
              participantType === 'adult-and-minors'
                ? 'border-blue-600 bg-blue-100 shadow-md'
                : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-sm'
            }`}
          >
            <div className="flex md:flex-col items-center md:space-y-3 space-x-3 md:space-x-0">
              <div className="flex items-center flex-shrink-0">
                <MdPerson
                  className={`w-8 h-8 md:w-12 md:h-12 ${
                    participantType === 'adult-and-minors'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                />
                <span className="text-lg md:text-2xl mx-0.5 md:mx-1 text-gray-400">
                  +
                </span>
                <MdPerson
                  className={`w-7 h-7 md:w-10 md:h-10 ${
                    participantType === 'adult-and-minors'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                />
              </div>
              <div className="text-left md:text-center flex-1">
                <h4
                  className={`font-semibold text-base md:text-lg ${
                    participantType === 'adult-and-minors'
                      ? 'text-blue-900'
                      : 'text-gray-700'
                  }`}
                >
                  Adult & Minor(s)
                </h4>
                <p className="text-xs md:text-sm text-gray-600 md:mt-1">
                  One adult + minors
                </p>
              </div>
              {participantType === 'adult-and-minors' && (
                <div className="md:absolute md:top-2 md:right-2">
                  <MdCheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
              )}
            </div>
          </button>
        </div>

        {/* Information Banner */}
        <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-100 border border-blue-300 rounded-lg">
          <div className="flex items-start space-x-2 md:space-x-3">
            <MdGavel className="w-4 h-4 md:w-5 md:h-5 text-blue-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs md:text-sm text-blue-900">
              <p className="font-medium mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-0.5 md:space-y-1 text-blue-800">
                <li className="leading-tight md:leading-normal">
                  Max <strong>one adult</strong> per waiver
                </li>
                <li className="leading-tight md:leading-normal">
                  Multiple minors allowed on guardian waiver
                </li>
                <li className="leading-tight md:leading-normal">
                  Adults must sign their own waivers
                </li>
                <li className="leading-tight md:leading-normal">
                  Guardian must sign for all minors
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Only show waiver content after participant type is selected */}
      {participantType !== null && (
        <div className="space-y-8">
          {/* Guardian Waiver Section for Minors Only */}
          {hasOnlyMinors && (
            <div className="border border-gray-200 rounded-lg p-2 md:p-6 bg-gray-50">
              <div className="flex items-center space-x-2 mb-4">
                <MdPerson className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Guardian Waiver for Minors
                </h3>
              </div>

              {/* Waiver Content */}
              <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <MdGavel className="text-blue-600" />
                    <h4 className="text-md font-medium text-gray-900">
                      Waiver
                    </h4>
                  </div>
                  <button
                    onClick={() => setShowExpandedWaiver(true)}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                  >
                    <MdExpandMore className="w-4 h-4" />
                    <span>View Full Waiver</span>
                  </button>
                </div>

                <div className="waiver-content prose prose-sm max-w-none">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">
                        Loading waiver...
                      </span>
                    </div>
                  ) : currentWaiver.waiverDetails ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: currentWaiver.waiverDetails,
                      }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-500 italic mb-4">
                        {error || 'No waiver content available'}
                      </div>
                      <button
                        onClick={refetchWaiver}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Loading...
                          </>
                        ) : (
                          'Reload Waiver'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="guardian-readWaiver"
                    checked={standaloneWaiver.hasReadWaiver}
                    onChange={e =>
                      setStandaloneWaiver(prev => ({
                        ...prev,
                        hasReadWaiver: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="guardian-readWaiver"
                    className="text-sm text-gray-700"
                  >
                    I have read and understand the complete liability waiver
                    above
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="guardian-acceptWaiver"
                    checked={standaloneWaiver.accepted}
                    onChange={e =>
                      setStandaloneWaiver(prev => ({
                        ...prev,
                        accepted: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="guardian-acceptWaiver"
                    className="text-sm text-gray-700"
                  >
                    I accept the terms and conditions of this liability waiver
                  </label>
                </div>
              </div>

              {/* Signature Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <MdEdit className="text-blue-600" />
                  <h4 className="font-medium text-gray-900">
                    Digital Signature
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name (as it appears on ID)
                    </label>
                    <input
                      type="text"
                      value={standaloneWaiver.signedBy}
                      onChange={e =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          signedBy: e.target.value,
                        }))
                      }
                      placeholder="Enter your full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship to Minor(s)
                    </label>
                    <select
                      value={standaloneWaiver.relationship}
                      onChange={e =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          relationship: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select relationship</option>
                      <option value="parent">Parent</option>
                      <option value="guardian">Legal Guardian</option>
                      <option value="grandparent">Grandparent</option>
                      <option value="sibling">Sibling (18 or older)</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={standaloneWaiver.email}
                      onChange={e =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="Enter your email address"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 ${
                        standaloneWaiver.email.trim() &&
                        !HelperMethods.isValidEmail(standaloneWaiver.email)
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                    {standaloneWaiver.email.trim() &&
                      !HelperMethods.isValidEmail(standaloneWaiver.email) && (
                        <p className="mt-1 text-sm text-red-600">
                          Please enter a valid email address
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={standaloneWaiver.phoneNumber}
                      onChange={e => {
                        const numericValue = e.target.value.replace(
                          /[^0-9]/g,
                          '',
                        );
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          phoneNumber: numericValue,
                        }));
                      }}
                      placeholder="Enter your phone number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Emergency Contact Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency Contact Phone Number
                  </label>
                  <input
                    type="tel"
                    value={standaloneWaiver.emergencyContactPhone}
                    onChange={e => {
                      const numericValue = e.target.value.replace(
                        /[^0-9]/g,
                        '',
                      );
                      setStandaloneWaiver(prev => ({
                        ...prev,
                        emergencyContactPhone: numericValue,
                      }));
                    }}
                    placeholder="Enter emergency contact phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Digital Signature
                  </label>
                  <div className="border border-gray-300 rounded-md p-2 bg-white">
                    <SignaturePad
                      height={200}
                      redrawOnResize
                      options={{
                        penColor: 'blue',
                        backgroundColor: 'white',
                      }}
                      compressionOptions={{
                        format: 'image/jpeg',
                        quality: 0.6,
                      }}
                      canvasProps={{
                        className:
                          'sigCanvas w-full h-48 border border-gray-200 rounded',
                      }}
                      onAutoSave={dataUrl =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          signature: dataUrl,
                        }))
                      }
                      autoSaveDelay={1000}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
                    <button
                      type="button"
                      onClick={() =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          signature: '',
                        }))
                      }
                      className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors min-h-[44px]"
                    >
                      <MdClear className="w-4 h-4" />
                      <span>Clear Signature</span>
                    </button>
                    <p className="text-xs text-gray-500 flex-1 text-center sm:text-left">
                      Sign above using your mouse or touch screen
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="mt-4 p-3 rounded-lg bg-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Waiver Status:</span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      standaloneWaiver.hasReadWaiver &&
                      standaloneWaiver.accepted &&
                      standaloneWaiver.signature.trim() &&
                      standaloneWaiver.signedBy.trim() &&
                      standaloneWaiver.relationship &&
                      standaloneWaiver.email.trim() &&
                      standaloneWaiver.phoneNumber.trim() &&
                      standaloneWaiver.emergencyContactPhone.trim()
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {standaloneWaiver.hasReadWaiver &&
                    standaloneWaiver.accepted &&
                    standaloneWaiver.signature.trim() &&
                    standaloneWaiver.signedBy.trim() &&
                    standaloneWaiver.relationship &&
                    standaloneWaiver.email.trim() &&
                    standaloneWaiver.phoneNumber.trim() &&
                    standaloneWaiver.emergencyContactPhone.trim()
                      ? '✓ Complete'
                      : '⚠ Incomplete'}
                  </span>
                </div>
              </div>

              {/* Add Minor Button for Standalone Guardian Waiver */}
              {skatersInfo.length === 0 && (
                <div className="mb-4">
                  <button
                    onClick={addNewMinor}
                    className="flex items-center justify-center space-x-2 px-4 py-3 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors min-h-[44px] w-full"
                  >
                    <MdAdd className="w-4 h-4" />
                    <span>Add Minor</span>
                  </button>
                </div>
              )}

              {/* Show minors being covered by guardian waiver */}
              {skatersInfo.length === 0 && skaterWaivers.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Minors covered by this guardian waiver:
                  </h4>
                  <div className="space-y-2">
                    {skaterWaivers.map((waiver, index) => (
                      <div
                        key={waiver.skaterId}
                        className="p-2 bg-white border border-gray-200 rounded"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Minor {index + 1}
                          </span>
                          {skaterWaivers.length > 1 && (
                            <button
                              onClick={() => removeMinor(waiver.skaterId)}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <MdRemove className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <input
                            type="text"
                            placeholder="First Name"
                            value={waiver.skaterName?.split(' ')[0] || ''}
                            onChange={e => {
                              const lastName =
                                waiver.skaterName
                                  ?.split(' ')
                                  .slice(1)
                                  .join(' ') || '';
                              updateMinorName(
                                waiver.skaterId,
                                e.target.value,
                                lastName,
                              );
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            placeholder="Last Name"
                            value={
                              waiver.skaterName
                                ?.split(' ')
                                .slice(1)
                                .join(' ') || ''
                            }
                            onChange={e => {
                              const firstName =
                                waiver.skaterName?.split(' ')[0] || '';
                              updateMinorName(
                                waiver.skaterId,
                                firstName,
                                e.target.value,
                              );
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="date"
                            placeholder="Date of Birth"
                            value={waiver.dateOfBirth || ''}
                            onChange={e =>
                              updateMinorDateOfBirth(
                                waiver.skaterId,
                                e.target.value,
                              )
                            }
                            min={HelperMethods.getMinDateOfBirth()}
                            max={HelperMethods.getMaxDateOfBirth()}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-800 mb-4">
                  Since all skaters are minors, a guardian must sign the waiver
                  on their behalf. Please provide your details and signature
                  below.
                </p>

                {/* Show all minors being covered */}
                {skatersInfo.length > 0 && skaterWaivers.length > 1 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      Minors covered by this guardian waiver:
                    </h4>
                    <div className="space-y-1">
                      {skaterWaivers.map(waiver => (
                        <div
                          key={waiver.skaterId}
                          className="text-sm text-blue-800"
                        >
                          • {waiver.skaterName} (Age:{' '}
                          {HelperMethods.calculateAge(waiver.dateOfBirth)})
                          {waiver.minors && waiver.minors.length > 0 && (
                            <span className="text-xs text-blue-600 ml-2">
                              (has {waiver.minors.length} minors assigned)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-blue-600">
                      ✓ All minors will be included in the generated PDF
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Debug: First minor has{' '}
                      {skaterWaivers[0]?.minors?.length || 0} minors assigned
                    </div>
                  </div>
                )}

                {/* Minors input fields when no skatersInfo */}
                {skatersInfo.length === 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      Minors covered by this guardian waiver:
                    </h4>
                    <div className="space-y-2">
                      {skaterWaivers.map((waiver, index) => (
                        <div
                          key={waiver.skaterId}
                          className="p-2 bg-white border border-gray-200 rounded"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Minor {index + 1}
                            </span>
                            {skaterWaivers.length > 1 && (
                              <button
                                onClick={() => removeMinor(waiver.skaterId)}
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <MdRemove className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="First Name"
                              value={waiver.skaterName?.split(' ')[0] || ''}
                              onChange={e => {
                                const lastName =
                                  waiver.skaterName
                                    ?.split(' ')
                                    .slice(1)
                                    .join(' ') || '';
                                updateMinorName(
                                  waiver.skaterId,
                                  e.target.value,
                                  lastName,
                                );
                              }}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="Last Name"
                              value={
                                waiver.skaterName
                                  ?.split(' ')
                                  .slice(1)
                                  .join(' ') || ''
                              }
                              onChange={e => {
                                const firstName =
                                  waiver.skaterName?.split(' ')[0] || '';
                                updateMinorName(
                                  waiver.skaterId,
                                  firstName,
                                  e.target.value,
                                );
                              }}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <input
                              type="date"
                              placeholder="Date of Birth"
                              value={waiver.dateOfBirth || ''}
                              onChange={e =>
                                updateMinorDateOfBirth(
                                  waiver.skaterId,
                                  e.target.value,
                                )
                              }
                              min={HelperMethods.getMinDateOfBirth()}
                              max={HelperMethods.getMaxDateOfBirth()}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Minor Button */}
                    <button
                      onClick={addNewMinor}
                      className="mt-2 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors w-full"
                    >
                      <MdAdd className="w-4 h-4" />
                      <span>Add Minor</span>
                    </button>
                  </div>
                )}

                {/* Waiver Content */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <MdGavel className="text-blue-600" />
                      <h4 className="text-md font-medium text-gray-900">
                        Waiver
                      </h4>
                    </div>
                    <button
                      onClick={() => setShowExpandedWaiver(true)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      <MdExpandMore className="w-4 h-4" />
                      <span>View Full Waiver</span>
                    </button>
                  </div>

                  <div className="waiver-content prose prose-sm max-w-none">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">
                          Loading waiver...
                        </span>
                      </div>
                    ) : currentWaiver.waiverDetails ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: currentWaiver.waiverDetails,
                        }}
                      />
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-500 italic mb-4">
                          {error || 'No waiver content available'}
                        </div>
                        <button
                          onClick={refetchWaiver}
                          disabled={loading}
                          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Loading...
                            </>
                          ) : (
                            'Reload Waiver'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Guardian Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Guardian Full Name (as it appears on ID)
                    </label>
                    <input
                      type="text"
                      value={skaterWaivers[0]?.signedBy || ''}
                      onChange={e => {
                        // Update all minors with the guardian's name
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'signedBy',
                            e.target.value,
                          );
                        });
                      }}
                      placeholder="Enter guardian's full name"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        !skaterWaivers[0]?.signedBy
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>

                  <div>
                    <MobileSelect
                      value={skaterWaivers[0]?.relationship || ''}
                      onChange={value => {
                        // Update all minors with the guardian's relationship
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'relationship',
                            value,
                          );
                        });
                      }}
                      options={[
                        { value: 'parent', label: 'Parent' },
                        { value: 'guardian', label: 'Legal Guardian' },
                        { value: 'grandparent', label: 'Grandparent' },
                        { value: 'sibling', label: 'Sibling (18 or older)' },
                        { value: 'other', label: 'Other' },
                      ]}
                      placeholder="Select relationship"
                      label="Relationship to Minors"
                      className={
                        !skaterWaivers[0]?.relationship ? 'border-red-500' : ''
                      }
                    />
                  </div>
                </div>

                {/* Guardian Email and Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Guardian Email Address
                    </label>
                    <input
                      type="email"
                      value={skaterWaivers[0]?.email || ''}
                      onChange={e => {
                        // Update all minors with the guardian's email
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'email',
                            e.target.value,
                          );
                        });
                      }}
                      placeholder="Enter guardian's email address"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 ${
                        !skaterWaivers[0]?.email ||
                        (skaterWaivers[0]?.email &&
                          !HelperMethods.isValidEmail(skaterWaivers[0]?.email))
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                    {skaterWaivers[0]?.email &&
                      !HelperMethods.isValidEmail(skaterWaivers[0]?.email) && (
                        <p className="mt-1 text-sm text-red-600">
                          Please enter a valid email address
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Guardian Phone Number
                    </label>
                    <input
                      type="tel"
                      value={skaterWaivers[0]?.phoneNumber || ''}
                      onChange={e => {
                        // Only allow numbers
                        const numericValue = e.target.value.replace(
                          /[^0-9]/g,
                          '',
                        );
                        // Update all minors with the guardian's phone number
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'phoneNumber',
                            numericValue,
                          );
                        });
                      }}
                      placeholder="Enter guardian's phone number"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        !skaterWaivers[0]?.phoneNumber
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                {/* Emergency Contact Phone Number */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency Contact Phone Number
                  </label>
                  <input
                    type="tel"
                    value={skaterWaivers[0]?.emergencyContactPhone || ''}
                    onChange={e => {
                      // Only allow numbers
                      const numericValue = e.target.value.replace(
                        /[^0-9]/g,
                        '',
                      );
                      // Update all minors with the guardian's emergency contact
                      skaterWaivers.forEach(waiver => {
                        updateSkaterWaiver(
                          waiver.skaterId,
                          'emergencyContactPhone',
                          numericValue,
                        );
                      });
                    }}
                    placeholder="Enter emergency contact phone number"
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      !skaterWaivers[0]?.emergencyContactPhone
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  />
                </div>

                {/* Guardian Signature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guardian Digital Signature
                  </label>
                  <div
                    className={`border rounded-md p-2 bg-white ${
                      !skaterWaivers.some(
                        waiver => !waiver.isAdult && waiver.signature,
                      )
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  >
                    <SignaturePad
                      height={200}
                      redrawOnResize
                      options={{
                        penColor: 'blue',
                        backgroundColor: 'white',
                      }}
                      compressionOptions={{
                        format: 'image/jpeg',
                        quality: 0.6,
                      }}
                      canvasProps={{
                        className:
                          'sigCanvas w-full h-48 border border-gray-200 rounded',
                      }}
                      onAutoSave={dataUrl => {
                        // Update all minors with the guardian's signature
                        skaterWaivers.forEach(waiver => {
                          handleAutoSave(waiver.skaterId, dataUrl);
                        });
                      }}
                      autoSaveDelay={1000}
                      ref={ref => {
                        if (ref) {
                          setSignaturePadRef('guardian', ref);
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Clear signature for all minors
                        skaterWaivers.forEach(waiver => {
                          clearSignature(waiver.skaterId);
                        });
                      }}
                      className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors min-h-[44px]"
                    >
                      <MdClear className="w-4 h-4" />
                      <span>Clear Signature</span>
                    </button>
                    <p className="text-xs text-gray-500 flex-1 text-center sm:text-left">
                      Sign above using your mouse or touch screen
                    </p>
                  </div>
                </div>

                {/* Guardian Checkboxes */}
                <div className="space-y-3 mt-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="guardian-read-waiver"
                      checked={skaterWaivers.every(
                        waiver => waiver.hasReadWaiver,
                      )}
                      onChange={e => {
                        // Update all minors with the guardian's read waiver status
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'hasReadWaiver',
                            e.target.checked,
                          );
                        });
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="guardian-read-waiver"
                      className="text-sm text-gray-700"
                    >
                      I have read and understand the complete liability waiver
                      above for all minors
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="guardian-accept-waiver"
                      checked={skaterWaivers.every(waiver => waiver.accepted)}
                      onChange={e => {
                        // Update all minors with the guardian's accept status
                        skaterWaivers.forEach(waiver => {
                          updateSkaterWaiver(
                            waiver.skaterId,
                            'accepted',
                            e.target.checked,
                          );
                        });
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="guardian-accept-waiver"
                      className="text-sm text-gray-700"
                    >
                      I accept the terms and conditions of this liability waiver
                      for all minors
                    </label>
                  </div>
                </div>

                {/* Guardian PDF Generation Button */}
                {skaterWaivers.every(
                  waiver =>
                    waiver.hasReadWaiver &&
                    waiver.accepted &&
                    waiver.signature.trim() &&
                    waiver.signedBy.trim() &&
                    waiver.relationship.trim() &&
                    waiver.email.trim() &&
                    waiver.phoneNumber.trim() &&
                    waiver.emergencyContactPhone.trim(),
                ) && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700 font-medium">
                        ✓ Guardian waiver complete! Generate PDF with all
                        minors:
                      </span>
                      <button
                        onClick={() => {
                          // Ensure minors are assigned before generating PDF
                          ensureMinorsAssigned();

                          console.log('Generating guardian PDF for:', {
                            firstMinor: skaterWaivers[0],
                            allSkaterWaivers: skaterWaivers.map(w => ({
                              name: w.skaterName,
                              id: w.skaterId,
                              minors: w.minors,
                              isAdult: w.isAdult,
                            })),
                          });

                          // Use setTimeout to ensure state updates are processed
                          setTimeout(() => {
                            downloadIndividualPDF(skaterWaivers[0]);
                          }, 100);
                        }}
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors flex items-center justify-center space-x-2 min-h-[44px]"
                      >
                        <MdDownload className="w-4 h-4" />
                        <span>Generate PDF</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Only show individual waiver sections if there are adults or if not all minors */}
          {!hasOnlyMinors &&
            !(participantType === 'minors' && skatersInfo.length === 0) &&
            (displayWaivers.length > 0 ? (
              displayWaivers.map((waiver, index) => (
                <div
                  key={waiver.skaterId}
                  className="border border-gray-200 rounded-lg p-2 md:p-6 bg-gray-50"
                >
                  {/* Skater Header - Only show if skatersInfo was provided */}
                  {skatersInfo.length > 0 && (
                    <div className="flex items-center space-x-2 mb-4">
                      <MdPerson className="text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">
                        Waiver for {waiver.skaterName}
                        {waiver.isAdult ? (
                          <span className="ml-2 text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                            Adult (18+)
                          </span>
                        ) : (
                          <span className="ml-2 text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
                            Minor (under 18)
                          </span>
                        )}
                      </h3>
                    </div>
                  )}

                  {/* Add Minor Button for Adults */}
                  {waiver.isAdult &&
                    getAvailableMinors(waiver.skaterId).length > 0 && (
                      <div className="mb-4 relative">
                        <button
                          onClick={() =>
                            setShowMinorDropdown(
                              showMinorDropdown === waiver.skaterId
                                ? null
                                : waiver.skaterId,
                            )
                          }
                          className="flex items-center justify-center space-x-2 px-4 py-3 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors min-h-[44px] w-full sm:w-auto"
                        >
                          <MdAdd className="w-4 h-4" />
                          <span>Add Minor to Waiver</span>
                        </button>

                        {/* Dropdown for selecting minors */}
                        {showMinorDropdown === waiver.skaterId && (
                          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10 minor-dropdown">
                            <div className="p-2">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Select a minor to add:
                              </h4>
                              <div className="space-y-1">
                                {getAvailableMinors(waiver.skaterId).map(
                                  minor => (
                                    <button
                                      key={minor.skaterId}
                                      onClick={() =>
                                        handleAddMinor(
                                          waiver.skaterId,
                                          minor.skaterId,
                                        )
                                      }
                                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                                    >
                                      {minor.skaterName} (Age:{' '}
                                      {HelperMethods.calculateAge(
                                        minor.dateOfBirth,
                                      )}
                                      )
                                    </button>
                                  ),
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Display Minors for this Adult */}
                  {waiver.isAdult && waiver.minors.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">
                        Minors covered by this waiver:
                      </h4>
                      <div className="space-y-2">
                        {waiver.minors.map(minor => {
                          const minorWaiver = skaterWaivers.find(
                            w => w.skaterId === minor,
                          );
                          return (
                            <div
                              key={minor}
                              className="flex items-center justify-between"
                            >
                              {skatersInfo.length > 0 ? (
                                <span className="text-sm text-blue-800">
                                  {minorWaiver?.skaterName}
                                </span>
                              ) : (
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2 mr-2">
                                  <input
                                    type="text"
                                    placeholder="First Name"
                                    value={
                                      minorWaiver?.skaterName?.split(' ')[0] ||
                                      ''
                                    }
                                    onChange={e => {
                                      const lastName =
                                        minorWaiver?.skaterName
                                          ?.split(' ')
                                          .slice(1)
                                          .join(' ') || '';
                                      updateMinorName(
                                        minor,
                                        e.target.value,
                                        lastName,
                                      );
                                    }}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Last Name"
                                    value={
                                      minorWaiver?.skaterName
                                        ?.split(' ')
                                        .slice(1)
                                        .join(' ') || ''
                                    }
                                    onChange={e => {
                                      const firstName =
                                        minorWaiver?.skaterName?.split(
                                          ' ',
                                        )[0] || '';
                                      updateMinorName(
                                        minor,
                                        firstName,
                                        e.target.value,
                                      );
                                    }}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                  <input
                                    type="date"
                                    placeholder="Date of Birth"
                                    value={minorWaiver?.dateOfBirth || ''}
                                    onChange={e =>
                                      updateMinorDateOfBirth(
                                        minor,
                                        e.target.value,
                                      )
                                    }
                                    min={HelperMethods.getMinDateOfBirth()}
                                    max={HelperMethods.getMaxDateOfBirth()}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              )}
                              <button
                                onClick={() =>
                                  skatersInfo.length > 0
                                    ? removeMinorFromWaiver(
                                        waiver.skaterId,
                                        minor,
                                      )
                                    : removeMinor(minor)
                                }
                                className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
                              >
                                <MdRemove className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {/* Add Minor Button - Only show when no skatersInfo */}
                      {skatersInfo.length === 0 && (
                        <button
                          onClick={addNewMinor}
                          className="mt-2 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors w-full"
                        >
                          <MdAdd className="w-4 h-4" />
                          <span>Add Minor</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Waiver Content */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <MdGavel className="text-blue-600" />
                        <h4 className="text-md font-medium text-gray-900">
                          Waiver
                        </h4>
                      </div>
                      <button
                        onClick={() => setShowExpandedWaiver(true)}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                      >
                        <MdExpandMore className="w-4 h-4" />
                        <span>View Full Waiver</span>
                      </button>
                    </div>

                    <div className="waiver-content prose prose-sm max-w-none">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">
                            Loading waiver...
                          </span>
                        </div>
                      ) : currentWaiver.waiverDetails ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: currentWaiver.waiverDetails,
                          }}
                        />
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-500 italic mb-4">
                            {error || 'No waiver content available'}
                          </div>
                          <button
                            onClick={refetchWaiver}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                          >
                            {loading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Loading...
                              </>
                            ) : (
                              'Reload Waiver'
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Waiver Modal */}
                  {showExpandedWaiver && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8">
                      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl transform transition-all duration-200 ease-in-out">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                          <div className="flex items-center space-x-2">
                            <MdGavel className="text-blue-600" />
                            <h3 className="text-lg font-medium text-gray-900">
                              Complete Liability Waiver
                            </h3>
                          </div>
                          <button
                            onClick={() => setShowExpandedWaiver(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <MdClear className="w-6 h-6" />
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                          <div className="waiver-content prose prose-lg max-w-none leading-relaxed">
                            {loading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="ml-2 text-gray-600">
                                  Loading waiver...
                                </span>
                              </div>
                            ) : currentWaiver.waiverDetails ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: currentWaiver.waiverDetails,
                                }}
                              />
                            ) : (
                              <div className="text-center py-8">
                                <div className="text-gray-500 italic mb-4">
                                  {error || 'No waiver content available'}
                                </div>
                                <button
                                  onClick={refetchWaiver}
                                  disabled={loading}
                                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                                >
                                  {loading ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                      Loading...
                                    </>
                                  ) : (
                                    'Reload Waiver'
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="p-6 border-t border-gray-200">
                          <button
                            onClick={() => setShowExpandedWaiver(false)}
                            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Only show waiver form for adults */}
                  {waiver.isAdult && (
                    <>
                      {/* Checkboxes */}
                      <div className="space-y-3 mb-4">
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id={`readWaiver-${waiver.skaterId}`}
                            checked={waiver.hasReadWaiver}
                            onChange={e =>
                              updateSkaterWaiver(
                                waiver.skaterId,
                                'hasReadWaiver',
                                e.target.checked,
                              )
                            }
                            className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 ${waiver.hasReadWaiver ? 'border-gray-300 rounded' : 'border-red-500 rounded'}`}
                          />
                          <label
                            htmlFor={`readWaiver-${waiver.skaterId}`}
                            className="text-sm text-gray-700"
                          >
                            I have read and understand the complete liability
                            waiver above
                            {skatersInfo.length > 0 &&
                              ` for ${waiver.skaterName}`}
                            {waiver.minors.length > 0 && (
                              <span> and the minors listed above</span>
                            )}
                          </label>
                        </div>

                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id={`acceptWaiver-${waiver.skaterId}`}
                            checked={waiver.accepted}
                            onChange={e =>
                              updateSkaterWaiver(
                                waiver.skaterId,
                                'accepted',
                                e.target.checked,
                              )
                            }
                            className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${waiver.accepted ? 'border-gray-300 rounded' : 'border-red-500 rounded'}`}
                          />
                          <label
                            htmlFor={`acceptWaiver-${waiver.skaterId}`}
                            className="text-sm text-gray-700"
                          >
                            I accept the terms and conditions of this liability
                            waiver
                            {skatersInfo.length > 0 &&
                              ` for ${waiver.skaterName}`}
                            {waiver.minors.length > 0 && (
                              <span> and the minors listed above</span>
                            )}
                          </label>
                        </div>
                      </div>

                      {/* Signature Section */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <MdEdit className="text-blue-600" />
                          <h4 className="font-medium text-gray-900">
                            Digital Signature
                          </h4>
                        </div>

                        {/* Warning banner for adults signing their own waiver - Only show when skatersInfo is provided */}
                        {skatersInfo.length > 0 &&
                          waiver.isAdult &&
                          waiver.minors.length === 0 && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                              <div className="flex items-center space-x-2">
                                <div className="flex-shrink-0">
                                  <svg
                                    className="h-5 w-5 text-red-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-red-800">
                                    Waiver can only be signed by the primary
                                    person
                                  </p>
                                  <p className="text-xs text-red-600 mt-1">
                                    {waiver.skaterName} must sign this waiver
                                    themselves as they are 18 or older
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Full Name (as it appears on ID)
                            </label>
                            <input
                              type="text"
                              value={waiver.signedBy}
                              onChange={e =>
                                updateSkaterWaiver(
                                  waiver.skaterId,
                                  'signedBy',
                                  e.target.value,
                                )
                              }
                              placeholder="Enter your full name"
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                !waiver.signedBy
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>

                          {/* Only show relationship field if this adult has minors */}
                          {waiver.minors.length > 0 && (
                            <div>
                              <MobileSelect
                                value={waiver.relationship}
                                onChange={value =>
                                  updateSkaterWaiver(
                                    waiver.skaterId,
                                    'relationship',
                                    value,
                                  )
                                }
                                options={[
                                  {
                                    value: 'self',
                                    label: 'Self (if 18 or older)',
                                  },
                                  { value: 'parent', label: 'Parent' },
                                  {
                                    value: 'guardian',
                                    label: 'Legal Guardian',
                                  },
                                  {
                                    value: 'grandparent',
                                    label: 'Grandparent',
                                  },
                                  {
                                    value: 'sibling',
                                    label: 'Sibling (18 or older)',
                                  },
                                  { value: 'other', label: 'Other' },
                                ]}
                                placeholder="Select relationship"
                                label="Relationship to Skater"
                                className={
                                  !waiver.relationship ? 'border-red-500' : ''
                                }
                              />
                            </div>
                          )}
                        </div>

                        {/* Email and Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={waiver.email}
                              onChange={e =>
                                updateSkaterWaiver(
                                  waiver.skaterId,
                                  'email',
                                  e.target.value,
                                )
                              }
                              placeholder="Enter your email address"
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 ${
                                !waiver.email ||
                                (waiver.email &&
                                  !HelperMethods.isValidEmail(waiver.email))
                                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                              }`}
                            />
                            {waiver.email &&
                              !HelperMethods.isValidEmail(waiver.email) && (
                                <p className="mt-1 text-sm text-red-600">
                                  Please enter a valid email address
                                </p>
                              )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number
                            </label>
                            <input
                              type="tel"
                              value={waiver.phoneNumber}
                              onChange={e => {
                                // Only allow numbers
                                const numericValue = e.target.value.replace(
                                  /[^0-9]/g,
                                  '',
                                );
                                updateSkaterWaiver(
                                  waiver.skaterId,
                                  'phoneNumber',
                                  numericValue,
                                );
                              }}
                              placeholder="Enter your phone number"
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                !waiver.phoneNumber
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>
                        </div>

                        {/* Emergency Contact Phone Number */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Emergency Contact Phone Number
                          </label>
                          <input
                            type="tel"
                            value={waiver.emergencyContactPhone}
                            onChange={e => {
                              // Only allow numbers
                              const numericValue = e.target.value.replace(
                                /[^0-9]/g,
                                '',
                              );
                              updateSkaterWaiver(
                                waiver.skaterId,
                                'emergencyContactPhone',
                                numericValue,
                              );
                            }}
                            placeholder="Enter emergency contact phone number"
                            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              !waiver.emergencyContactPhone
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Digital Signature
                          </label>
                          <div
                            className={`border rounded-md p-2 bg-white ${
                              !waiver.signature
                                ? 'border-red-500'
                                : 'border-gray-300'
                            }`}
                          >
                            <SignaturePad
                              height={200}
                              redrawOnResize
                              options={{
                                penColor: 'blue',
                                backgroundColor: 'white',
                              }}
                              compressionOptions={{
                                format: 'image/jpeg',
                                quality: 0.6,
                              }}
                              canvasProps={{
                                className:
                                  'sigCanvas w-full h-48 border border-gray-200 rounded',
                              }}
                              onAutoSave={dataUrl =>
                                handleAutoSave(waiver.skaterId, dataUrl)
                              }
                              autoSaveDelay={1000}
                              ref={ref => {
                                if (ref) {
                                  setSignaturePadRef(waiver.skaterId, ref);
                                }
                              }}
                            />
                          </div>
                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
                            <button
                              type="button"
                              onClick={() => clearSignature(waiver.skaterId)}
                              className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors min-h-[44px]"
                            >
                              <MdClear className="w-4 h-4" />
                              <span>Clear Signature</span>
                            </button>
                            <p className="text-xs text-gray-500 flex-1 text-center sm:text-left">
                              Sign above using your mouse or touch screen
                            </p>
                          </div>
                          {waiver.signature && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                              <p className="text-xs text-green-700">
                                ✓ Signature captured successfully
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Waiver Status */}
                      <div className="mt-4 p-3 rounded-lg bg-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            Waiver Status:
                          </span>
                          <div className="flex items-center space-x-2">
                            {/* {waiver.hasReadWaiver &&
                        waiver.accepted &&
                        waiver.signature.trim() &&
                        waiver.signedBy.trim() &&
                        waiver.relationship &&
                        waiver.emergencyContactPhone.trim() && (
                          <button
                            onClick={() => downloadIndividualPDF(waiver)}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors flex items-center justify-center space-x-2 min-h-[44px]"
                          >
                            <MdDownload className="w-4 h-4" />
                            <span>Generate PDF</span>
                          </button>
                        )} */}
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                waiver.hasReadWaiver &&
                                waiver.accepted &&
                                waiver.signature.trim() &&
                                waiver.signedBy.trim() &&
                                (waiver.minors.length === 0
                                  ? waiver.relationship === 'self'
                                  : waiver.relationship) &&
                                waiver.email.trim() &&
                                waiver.phoneNumber.trim() &&
                                waiver.emergencyContactPhone.trim()
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {waiver.hasReadWaiver &&
                              waiver.accepted &&
                              waiver.signature.trim() &&
                              waiver.signedBy.trim() &&
                              (waiver.minors.length === 0
                                ? waiver.relationship === 'self'
                                : waiver.relationship) &&
                              waiver.email.trim() &&
                              waiver.phoneNumber.trim() &&
                              waiver.emergencyContactPhone.trim()
                                ? '✓ Complete'
                                : '⚠ Incomplete'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Show message for minors */}
                  {!waiver.isAdult && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm text-yellow-800">
                        This minor needs to be added to an adult's waiver.
                        Please use the "Add Minor to Waiver" button on an
                        adult's waiver section.
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              // Show the actual waiver form structure for standalone waivers
              <div className="border border-gray-200 rounded-lg p-2 md:p-6 bg-gray-50">
                {/* Waiver Content */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <MdGavel className="text-blue-600" />
                      <h4 className="text-md font-medium text-gray-900">
                        Waiver
                      </h4>
                    </div>
                    <button
                      onClick={() => setShowExpandedWaiver(true)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      <MdExpandMore className="w-4 h-4" />
                      <span>View Full Waiver</span>
                    </button>
                  </div>

                  <div className="waiver-content prose prose-sm max-w-none">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">
                          Loading waiver...
                        </span>
                      </div>
                    ) : currentWaiver.waiverDetails ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: currentWaiver.waiverDetails,
                        }}
                      />
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-500 italic mb-4">
                          {error || 'No waiver content available'}
                        </div>
                        <button
                          onClick={refetchWaiver}
                          disabled={loading}
                          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Loading...
                            </>
                          ) : (
                            'Reload Waiver'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="standalone-readWaiver"
                      checked={standaloneWaiver.hasReadWaiver}
                      onChange={e =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          hasReadWaiver: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="standalone-readWaiver"
                      className="text-sm text-gray-700"
                    >
                      I have read and understand the complete liability waiver
                      above
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="standalone-acceptWaiver"
                      checked={standaloneWaiver.accepted}
                      onChange={e =>
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          accepted: e.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="standalone-acceptWaiver"
                      className="text-sm text-gray-700"
                    >
                      I accept the terms and conditions of this liability waiver
                    </label>
                  </div>
                </div>

                {/* Signature Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <MdEdit className="text-blue-600" />
                    <h4 className="font-medium text-gray-900">
                      Digital Signature
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name (as it appears on ID)
                      </label>
                      <input
                        type="text"
                        value={standaloneWaiver.signedBy}
                        onChange={e =>
                          setStandaloneWaiver(prev => ({
                            ...prev,
                            signedBy: e.target.value,
                          }))
                        }
                        placeholder="Enter your full name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {participantType === 'adult-and-minors' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Relationship to Skater
                        </label>
                        <select
                          value={standaloneWaiver.relationship}
                          onChange={e =>
                            setStandaloneWaiver(prev => ({
                              ...prev,
                              relationship: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select relationship</option>
                          <option value="self">Self (if 18 or older)</option>
                          <option value="parent">Parent</option>
                          <option value="guardian">Legal Guardian</option>
                          <option value="grandparent">Grandparent</option>
                          <option value="sibling">Sibling (18 or older)</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Email and Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={standaloneWaiver.email}
                        onChange={e =>
                          setStandaloneWaiver(prev => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter your email address"
                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 ${
                          standaloneWaiver.email.trim() &&
                          !HelperMethods.isValidEmail(standaloneWaiver.email)
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      />
                      {standaloneWaiver.email.trim() &&
                        !HelperMethods.isValidEmail(standaloneWaiver.email) && (
                          <p className="mt-1 text-sm text-red-600">
                            Please enter a valid email address
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={standaloneWaiver.phoneNumber}
                        onChange={e => {
                          // Only allow numbers
                          const numericValue = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                          setStandaloneWaiver(prev => ({
                            ...prev,
                            phoneNumber: numericValue,
                          }));
                        }}
                        placeholder="Enter your phone number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Emergency Contact Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact Phone Number
                    </label>
                    <input
                      type="tel"
                      value={standaloneWaiver.emergencyContactPhone}
                      onChange={e => {
                        // Only allow numbers
                        const numericValue = e.target.value.replace(
                          /[^0-9]/g,
                          '',
                        );
                        setStandaloneWaiver(prev => ({
                          ...prev,
                          emergencyContactPhone: numericValue,
                        }));
                      }}
                      placeholder="Enter emergency contact phone number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Digital Signature
                    </label>
                    <div className="border border-gray-300 rounded-md p-2 bg-white">
                      <SignaturePad
                        height={200}
                        redrawOnResize
                        options={{
                          penColor: 'blue',
                          backgroundColor: 'white',
                        }}
                        compressionOptions={{
                          format: 'image/jpeg',
                          quality: 0.6,
                        }}
                        canvasProps={{
                          className:
                            'sigCanvas w-full h-48 border border-gray-200 rounded',
                        }}
                        onAutoSave={dataUrl =>
                          setStandaloneWaiver(prev => ({
                            ...prev,
                            signature: dataUrl,
                          }))
                        }
                        autoSaveDelay={1000}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
                      <button
                        type="button"
                        onClick={() =>
                          setStandaloneWaiver(prev => ({
                            ...prev,
                            signature: '',
                          }))
                        }
                        className="flex items-center justify-center space-x-1 px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors min-h-[44px]"
                      >
                        <MdClear className="w-4 h-4" />
                        <span>Clear Signature</span>
                      </button>
                      <p className="text-xs text-gray-500 flex-1 text-center sm:text-left">
                        Sign above using your mouse or touch screen
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="mt-4 p-3 rounded-lg bg-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Waiver Status:
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        standaloneWaiver.hasReadWaiver &&
                        standaloneWaiver.accepted &&
                        standaloneWaiver.signature.trim() &&
                        standaloneWaiver.signedBy.trim() &&
                        (participantType === 'adult' ||
                          standaloneWaiver.relationship) &&
                        standaloneWaiver.email.trim() &&
                        standaloneWaiver.phoneNumber.trim() &&
                        standaloneWaiver.emergencyContactPhone.trim()
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {standaloneWaiver.hasReadWaiver &&
                      standaloneWaiver.accepted &&
                      standaloneWaiver.signature.trim() &&
                      standaloneWaiver.signedBy.trim() &&
                      (participantType === 'adult' ||
                        standaloneWaiver.relationship) &&
                      standaloneWaiver.email.trim() &&
                      standaloneWaiver.phoneNumber.trim() &&
                      standaloneWaiver.emergencyContactPhone.trim()
                        ? '✓ Complete'
                        : '⚠ Incomplete'}
                    </span>
                  </div>
                </div>
              </div>
            ))}

          {submitError && (
            <div className="w-full sm:w-auto px-4 py-2 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
              {submitError}
            </div>
          )}
        </div>
      )}

      {/* Navigation Buttons - Only show after participant type is selected */}
      {participantType !== null && (
        <div className="flex flex-col sm:flex-row justify-between pt-6 space-y-3 sm:space-y-0 wrap-reverse ">
          {onPrevious && (
            <button
              onClick={onPrevious}
              className="w-full mt-6 md:mt-0 sm:w-auto px-6 py-3 text-gray-700 bg-gray-200 font-medium rounded-lg hover:bg-gray-300 active:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors min-h-[48px] flex items-center justify-center order-2 sm:order-1"
            >
              Back
            </button>
          )}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 order-1 sm:order-2">
            {/* {isFormValid() && (
            <button
              onClick={downloadAllIndividualPDFs}
              className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center justify-center space-x-2 min-h-[48px]"
            >
              <MdDownload className="w-5 h-5" />
              <span>Generate All PDFs</span>
            </button>
          )} */}

            <button
              onClick={handleContinue}
              disabled={!isFormValid() || isSubmitting}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 min-h-[48px]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Generating PDFs...</span>
                </>
              ) : (
                <>
                  <MdCheckCircle className="w-5 h-5" />
                  <span>{continueButtonText}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
